// src/pages/ServicesPage.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import servicesHeroBg from '../assets/images/services/tech-guy-cartoon.jpg';

const ServicesPage = () => {
  const { isDark } = useTheme();

  const services = [
    {
      id: 'custom-software',
      title: 'Custom Software Development',
      icon: '💻',
      description: 'Tailored software solutions designed to address unique business challenges and operational needs. We build scalable, secure, and innovative systems that give you a competitive edge.',
      features: ['Tailored Solutions', 'Agile Development', 'Enterprise Security', 'Full-Cycle Support'],
      link: '/services/custom-software-development',
      badge: 'CUSTOM DEVELOPMENT'
    },
    {
      id: 'management-consulting',
      title: 'Management System Consulting',
      icon: '📋',
      description: 'Strategic optimization of business processes through expert management systems implementation. We help you achieve operational excellence and sustainable growth.',
      features: ['Process Optimization', 'Strategic Planning', 'Performance Metrics', 'Change Management'],
      link: '/services/management-system-consulting',
      badge: 'MANAGEMENT CONSULTING'
    },
    {
      id: 'ecommerce',
      title: 'Ecommerce Applications',
      icon: '🛒',
      description: 'Powerful digital commerce platforms that deliver seamless shopping experiences. Expand your market reach and boost sales with our custom ecommerce solutions.',
      features: ['Seamless Shopping', 'Secure Payments', 'Mobile Optimized', 'Analytics Dashboard'],
      link: '/services/ecommerce-applications',
      badge: 'ECOMMERCE SOLUTIONS'
    },
    {
      id: 'software-licensing',
      title: 'Software Licensing',
      icon: '📜',
      description: 'Flexible licensing options for ready-made software products. Deploy quickly, scale easily, and get enterprise-grade solutions without the custom development timeline.',
      features: ['Flexible Licensing', 'Ready-Made Products', 'Customizable Options', 'Enterprise Ready'],
      link: '/services/software-licensing',
      badge: 'SOFTWARE LICENSING'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section with Background Image */}
      <section className={`relative py-16 sm:py-20 lg:py-24 overflow-hidden ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src={servicesHeroBg}
            alt="DOROTS services background"
            className="w-full h-full object-cover object-center"
          />
          {/* Dark/Light overlay for text readability */}
          <div className={`absolute inset-0 ${
            isDark 
              ? 'bg-gradient-to-r from-black/85 via-black/70 to-black/50' 
              : 'bg-gradient-to-r from-black/85 via-black/70 to-black/50'
          }`} />
        </div>

        {/* Glow effects - only in dark mode */}
        {isDark && (
          <>
            <div className="absolute top-[-200px] left-[-100px] w-[500px] h-[500px] bg-blue-700/15 rounded-full blur-3xl z-0" />
            <div className="absolute bottom-[-250px] right-[-150px] w-[600px] h-[600px] bg-blue-700/15 rounded-full blur-3xl z-0" />
          </>
        )}
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="mb-4">
              <span className={`text-xs sm:text-sm tracking-[0.2em] font-medium px-4 py-2 rounded-full inline-block backdrop-blur-sm ${
                isDark 
                  ? 'text-blue-700 bg-blue-700/20 border border-blue-700/30' 
                  : 'text-blue-700 bg-white border border-blue-200'
              }`}>
                OUR SERVICES
              </span>
            </div>
            
            {/* Heading */}
            <h1 className={`text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight ${isDark ? 'text-white' : 'text-white'}`}>
              What We{' '}
              <span className="bg-gradient-to-r from-blue-700 via-blue-600 to-blue-700 bg-clip-text text-transparent">
                Deliver
              </span>
            </h1>
            
            {/* Description */}
            <p className={`text-lg sm:text-xl leading-relaxed ${isDark ? 'text-gray-200' : 'text-white'}`}>
              Comprehensive technology solutions designed to transform your business operations
              and drive sustainable growth
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className={`py-16 sm:py-20 ${isDark ? 'bg-black' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Link to={service.link} key={index}>
                <div className={`group p-8 rounded-2xl transition-all duration-300 hover:transform hover:scale-[1.02] ${
                  isDark 
                    ? 'bg-white/5 border border-white/10 hover:border-blue-700/30 hover:bg-white/10' 
                    : 'bg-gray-50 border border-gray-200 hover:border-blue-700 hover:shadow-lg'
                }`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-5xl">{service.icon}</div>
                    <div className={`text-xs font-medium px-3 py-1 rounded-full ${
                      isDark 
                        ? 'bg-blue-700/20 text-blue-700' 
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {service.badge}
                    </div>
                  </div>
                  
                  <h3 className={`text-2xl font-bold mb-3 group-hover:text-blue-700 transition-colors ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {service.title}
                  </h3>
                  
                  <p className={`mb-4 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {service.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mt-4">
                    {service.features.map((feature, idx) => (
                      <span key={idx} className={`text-xs px-2 py-1 rounded-full ${
                        isDark 
                          ? 'bg-white/5 text-gray-400' 
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {feature}
                      </span>
                    ))}
                  </div>
                  
                  <div className="mt-6 flex items-center gap-2 text-blue-700 group-hover:gap-3 transition-all">
                    <span className="text-sm font-medium">Learn More</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className={`py-16 sm:py-20 ${isDark ? 'bg-gray-900/30' : 'bg-blue-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`rounded-3xl p-8 md:p-12 text-center ${
            isDark 
              ? 'bg-gradient-to-r from-blue-700/10 to-blue-700/10 border border-white/10' 
              : 'bg-gradient-to-r from-blue-50 to-blue-50 border border-blue-100'
          }`}>
            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Why Choose <span className="bg-gradient-to-r from-blue-700 to-blue-600 bg-clip-text text-transparent">DOROTS</span>
            </h2>
            <p className={`text-lg mb-12 max-w-2xl mx-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              We combine technical expertise with business acumen to deliver exceptional results
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center group">
                <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                  isDark 
                    ? 'bg-blue-700/20 group-hover:bg-blue-700/30' 
                    : 'bg-blue-100 group-hover:bg-blue-200'
                }`}>
                  <span className={`text-3xl ${isDark ? 'text-blue-700' : 'text-blue-700'}`}>✓</span>
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Proven Expertise
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Years of software development experience and successful projects
                </p>
              </div>
              
              <div className="text-center group">
                <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                  isDark 
                    ? 'bg-blue-700/20 group-hover:bg-blue-700/30' 
                    : 'bg-blue-100 group-hover:bg-blue-200'
                }`}>
                  <span className={`text-3xl ${isDark ? 'text-blue-700' : 'text-blue-700'}`}>🎯</span>
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Customized Solutions
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Tailored solutions designed for your unique business needs
                </p>
              </div>
              
              <div className="text-center group">
                <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 ${
                  isDark 
                    ? 'bg-blue-700/20 group-hover:bg-blue-700/30' 
                    : 'bg-blue-100 group-hover:bg-blue-200'
                }`}>
                  <span className={`text-3xl ${isDark ? 'text-blue-700' : 'text-blue-700'}`}>🔄</span>
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Comprehensive Support
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  From consultation through post-implementation monitoring
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 ${isDark ? 'bg-black' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`text-2xl sm:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Ready to Transform Your Business?
          </h2>
          <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Let's discuss which service is right for your needs
          </p>
          <Link to="/contact">
            <button className="px-8 py-3 rounded-xl bg-gradient-to-r from-blue-700 to-blue-600 text-white font-semibold hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
              Contact Our Team
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;